/**
 * MultiSelectDropdown Component
 * Reusable multi-select dropdown component with new design system
 * Note: shadcn Select doesn't support multi-select, so we keep custom implementation
 */

'use client'

import { useState, useRef, useEffect } from 'react'
import { ChevronDown, Check, X } from 'lucide-react'
import { cn } from '@/lib/utils'

interface DropdownOption {
  value: string
  label: string
}

interface MultiSelectDropdownProps {
  label?: string
  selectedValues: string[]
  options: DropdownOption[]
  onChange: (values: string[]) => void
  placeholder?: string
  clearLabel?: string
  className?: string
  disabled?: boolean
}

export default function MultiSelectDropdown({
  label,
  selectedValues,
  options,
  onChange,
  placeholder = 'Select options',
  clearLabel = 'Clear all',
  className = '',
  disabled = false,
}: MultiSelectDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const toggleOption = (value: string) => {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter((v) => v !== value))
    } else {
      onChange([...selectedValues, value])
    }
  }

  const clearAll = () => {
    onChange([])
    setIsOpen(false)
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  // Get selected labels for display
  const selectedLabels = selectedValues
    .map((v) => options.find((o) => o.value === v)?.label)
    .filter(Boolean)

  return (
    <div className={cn('relative w-full', className)} ref={dropdownRef}>
      {label && (
        <label className="block text-xs sm:text-sm font-medium text-muted-foreground mb-1.5">
          {label}
        </label>
      )}
      <button
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        type="button"
        className={cn(
          // Base styles
          'flex h-9 w-full items-center justify-between whitespace-nowrap rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm ring-offset-background',
          // Focus
          'focus:outline-none focus:ring-1 focus:ring-ring',
          // Disabled
          'disabled:cursor-not-allowed disabled:opacity-50',
          // Mobile optimizations
          'touch-manipulation transition-colors duration-150',
          'active:bg-muted/50',
          // Open state
          isOpen && 'ring-1 ring-ring'
        )}
      >
        <span
          className={cn(
            'flex-1 text-left truncate',
            selectedValues.length === 0 && 'text-muted-foreground'
          )}
        >
          {selectedValues.length === 0
            ? placeholder
            : selectedLabels.length <= 2
              ? selectedLabels.join(', ')
              : `${selectedLabels.length} selected`}
        </span>
        <ChevronDown
          className={cn(
            'h-4 w-4 opacity-50 shrink-0 ml-2 transition-transform duration-150',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {/* Dropdown Content */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-card rounded-md border border-border shadow-md z-50 overflow-hidden animate-in fade-in-0 zoom-in-95 slide-in-from-top-2">
          <div className="p-1 max-h-[300px] overflow-y-auto">
            {options.map((option) => {
              const isSelected = selectedValues.includes(option.value)

              return (
                <button
                  key={option.value}
                  onClick={(e) => {
                    e.stopPropagation()
                    toggleOption(option.value)
                  }}
                  type="button"
                  className={cn(
                    // Base styles
                    'relative flex w-full items-center rounded-sm py-2 pl-2 pr-8 text-sm outline-none',
                    // Hover/focus
                    'hover:bg-muted focus:bg-muted',
                    // Selected
                    isSelected && 'bg-primary/5',
                    // Mobile optimizations
                    'touch-manipulation transition-colors duration-150'
                  )}
                >
                  <span className="text-foreground">{option.label}</span>
                  {isSelected && (
                    <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
                      <Check className="h-4 w-4 text-primary" />
                    </span>
                  )}
                </button>
              )
            })}

            {selectedValues.length > 0 && (
              <>
                <div className="h-px bg-border my-1 -mx-1" />
                <button
                  onClick={clearAll}
                  type="button"
                  className="flex w-full items-center gap-2 rounded-sm py-2 px-2 text-sm text-muted-foreground hover:text-destructive hover:bg-destructive/10 touch-manipulation transition-colors duration-150"
                >
                  <X className="h-3.5 w-3.5" />
                  {clearLabel}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
